import {
  useState, useCallback, useMemo
} from 'react'
import dynamic from 'next/dynamic'
import Head from 'components/Head'
import Navbar from 'components/Navbar'
import Footer from 'components/Footer'
import PromosProvider from 'components/Promos'
import company from 'config/company.json'
import { initHeaders } from 'utils/request/headers'
import {
  withLangStaticPaths, withErrorStaticProps, Props as StaticProps
} from 'utils/request/ssg'
import { loadTranslations } from 'utils/request/i18n'
import {
  NavigationProvider,
  useCompanySchema,
  useLocalizedUrl,
  getDestination,
  listDestinationChildren,
  getSeoMeta,
  getFilterLimits,
  parseBoatFiltersQueryString,
  listHotDestinations,
  listBoats,
  listMarkers,
  listMarinas,
  useTranslation,
  useLanguage,
  useLocaleState,
  OrganicLandingSearchProvider,
  OrganicLandingSearch,
  SkipperMessage,
  OrganicLandingHeader,
  OrganicLandingDestinationList,
  OrganicLandingMarinaList,
  OrganicLandingDestinationBoatTypes,
  withMapContext
} from '@sailogy/shipwright'
import {
  BoatFilters, FilterLimits, BoatListed, ListMeta, MarkerSimple,
  Destination, DestinationListed, MarinaNested, SeoMeta
} from '@sailogy/types'
import { K, i18nPath } from 'i18n/search_destination'

const LandingEnquiryModal = dynamic(
  () => import('@sailogy/containers/Enquiry/Landing/Modal/Timer'),
  { ssr: false }
)

export interface Props {
  filters: Partial<BoatFilters>
  limits: FilterLimits
  boats: BoatListed[]
  meta: ListMeta
  markers: MarkerSimple[]
  marinas: MarinaNested[]
  destination: Destination
  seo: SeoMeta
  children: DestinationListed[]
}


const DestinationSearchPage: React.FC<Props> = ({
  filters: defaultFilters, limits, boats, meta, markers, marinas: ssrMarinas,
  destination: ssrDestination, seo: ssrSeo, children: ssrChildren
}) => {
  const t = useTranslation<K>()
  const localizedUrl = useLocalizedUrl()
  const [language] = useLanguage()
  const structuredData = useCompanySchema(company)
  const [loading, setLoading] = useState<boolean>(false)
  const filters = (typeof window !== 'undefined' && window.location.search)
    ? parseBoatFiltersQueryString(window.location.search.slice(1))
    : defaultFilters
  const [destination, setDestination] = useLocaleState<Destination>(
    ssrDestination, async (signal: AbortSignal) => (
      getDestination(ssrDestination.slug[language], { signal })
    )
  )
  const [seo, setSeo] = useLocaleState<SeoMeta>(
    ssrSeo, async (signal: AbortSignal) => (
      getSeoMeta(company.website, language, `/destination/${destination.slug[language]}/`, { signal })
    )
  )
  const [children, setChildren] = useLocaleState<DestinationListed[]>(
    ssrChildren, async (signal: AbortSignal) => (
      listDestinationChildren(destination.slug[language], { signal })
        .then(({ results }) => results)
    )
  )
  const [marinas, setMarinas] = useLocaleState<MarinaNested[]>(
    ssrMarinas, async (signal: AbortSignal) => (
      listMarinas({ destination: destination.slug[language] }, { signal })
        .then(({ results }) => results)
    )
  )

  const handleChange = useCallback(async (newSlug: string) => {
    // Load new destination data
    setLoading(true)
    const [
      newDestination,
      newSeo,
      { results: newChildren },
      { results: newMarinas }
    ] = await Promise.all([
      getDestination(newSlug),
      getSeoMeta(company.website, language, `/destination/${newSlug}/`),
      listDestinationChildren(newSlug),
      listMarinas({ destination: newSlug })
    ])
    // Update values
    setDestination(newDestination)
    setSeo(newSeo)
    setChildren(newChildren)
    setMarinas(newMarinas)
    setLoading(false)
  }, [setDestination])

  const breadcrumbs = useMemo(() => [
    { href: localizedUrl('/'), label: t('Home') },
    { href: localizedUrl('/destinations'), label: t('Destinations') },
    ...destination.breadcrumbs.map((item) => ({
      href: localizedUrl('/destinations/:slug', { slug: item.slug }),
      label: item.label,
      onClick: () => handleChange(item.slug)
    }))
  ], [destination])

  return (
    <NavigationProvider path="/destinations/:slug" vars={{ slug: destination.slug }}>
      <Head
        cleanTitle={!!destination.seo.meta_title}
        title={seo.meta_title || destination.seo.meta_title || t('{destination} Best Yacht Charter & Sailing Holidays {year}', { destination: destination.name, year: new Date().getFullYear() })}
        description={seo.meta_description || destination.seo.meta_description || t('Find out the best yacht charter for holidays in {destination}. Hire sailing boat, catamaran, powerboat. You can rent skippered or bareboats. Book now!', { destination: destination.name })}
        image={destination.image}
        schema={structuredData}
        noIndex={filters !== defaultFilters}
      />
      <Navbar />
      <LandingEnquiryModal timer={10000} />
      <PromosProvider>
        <OrganicLandingSearchProvider filters={filters}>
          <OrganicLandingSearch
            limits={limits}
            boats={boats}
            meta={meta}
            markers={markers}
          >
            <OrganicLandingHeader
              title={destination.seo.page_title || t('Yacht charter {destination}', { destination: destination.name })}
              description={t('%d boat in this area', { value: destination.boat_count })}
              breadcrumbs={breadcrumbs}
            />
            <SkipperMessage />
          </OrganicLandingSearch>
          {(!loading && children.length > 0) && (
          <OrganicLandingDestinationList
            territory={destination.name}
            list={children}
            onChange={handleChange}
          />
          )}
          {(!loading && marinas.length > 0) && (
          <OrganicLandingMarinaList
            territory={destination.name}
            list={marinas}
          />
          )}
          {!loading && (
          <OrganicLandingDestinationBoatTypes
            title={t('Different boat types %(destination)s', { destination: t('in {destination}', { destination: destination.name }) })}
            path={`/destinations/${destination.slug[language]}/:type`}
          />
          )}
        </OrganicLandingSearchProvider>
      </PromosProvider>
      <Footer />
    </NavigationProvider>
  )
}


// Build SSG pages in home page for each language
export const getStaticPaths = withLangStaticPaths(async ({ locale }) => {
  const { results } = await listHotDestinations(
    undefined, initHeaders({ locale })
  )
  return results.slice(0, 10).map(({ slug }) => ({ locale, slug: [slug] }))
}, true)

export const getStaticProps = withErrorStaticProps<Props>(async (ctx) => {
  // EG: /it/destination/grecia/zante
  // WARN: Sitemap has slugs with id value in the end
  const slug: string = (
    (slugs) => slugs[slugs.length - 1].replace(/-\d+$/, '')
  )(ctx.params!.slug as string[])
  const [
    i18n,
    limits,
    destination,
    seo,
    { results: children },
    { results: marinas }
  ] = await Promise.all([
    loadTranslations(ctx.locale!, i18nPath),
    getFilterLimits().catch(() => ({ })),
    getDestination(slug, initHeaders(ctx)),
    getSeoMeta(company.website, ctx.locale!, `/destination/${slug}/`),
    listDestinationChildren(slug, null, initHeaders(ctx)),
    listMarinas({ destination: slug }, initHeaders(ctx))
  ])
  if (destination == null) {
    return { props: ctx.params, notFound: true }
  }
  // Prefetch boats and markers based
  const filters: Partial<BoatFilters> = {
    search_where: destination.name,
    weeks_count: 1
  }

  const [{ results: boats, ...res }, markers] = await Promise.all([
    listBoats(filters, initHeaders(ctx))
      .catch(() => ({ results: [], count: 0 })),
    listMarkers(filters, initHeaders(ctx)).catch(() => [])
  ])
  const meta: ListMeta = {
    count: res.count,
    nextPage: ('next' in res && res.next != null) ? 2 : null!,
    previousPage: null!
  }

  return {
    props: {
      ...ctx.params,
      i18n,
      i18nPath,
      filters,
      limits,
      boats,
      meta,
      markers,
      marinas,
      destination,
      children,
      seo
    } as StaticProps<Props>,
    // Invalidate when list is empty
    revalidate: boats.length === 0 ? 1 : 86400 // 24h
  }
})

export default withMapContext(DestinationSearchPage)
